import {EvalOrderResult, GeometryCopy, ItemData} from "../worker/RenderWorker.ts";
import {getParent} from "./PureElementUtils.ts";
import {RenderTraversalUtils} from "./RenderTraversalUtils.ts";

type LayoutRect = { left: number; top: number; width: number; height: number };

type DedupGroup = {
    key: string;
    members: string[];
};

export class Renderer {
    private anchors: Map<string, string[]>
    private itemData: Map<string, ItemData>
    private layout_id: string | null;
    private width: number;
    private height: number;
    private layoutRectMemo: Map<string, LayoutRect | null>;
    private measureCtx: OffscreenCanvasRenderingContext2D | null;
    private dedupGroups: DedupGroup[];
    /** ids already warned about an unusable measurement font, so the warning fires once per item. */
    private badFontLogged: Set<string>;
    /** Last count published per sheet; dedups SUPPORTED_ROWS. */
    private publishedSupportedRows: Map<string, number>;
    /* see docs/EXTRACTED_COMMENTS.md#c791 */
    private structureDirty = true;
    private evalOrder: EvalOrderResult = {ok: true, order: []};
    private reachability: Map<string, Set<string>> = new Map();
    /** Per-id segment count and numeric tail; ids never change. */
    private idSegmentsCache: Map<string, number> = new Map();

    constructor() {
        this.anchors = new Map();
        this.itemData = new Map();
        this.layout_id = null;
        this.width = 1920;
        this.height = 1080;
        this.layoutRectMemo = new Map();
        this.measureCtx = new OffscreenCanvas(1, 1).getContext("2d");
        this.dedupGroups = [];
        this.badFontLogged = new Set();
        this.publishedSupportedRows = new Map();
    }

    public updateAnchors(anchors: Map<string, string[]>) {
        this.anchors = anchors;
        this.structureDirty = true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c211 */
    private rebuildAnchorsFromItems() {
        this.anchors.clear();
        for (const item of this.itemData.values()) {
            const targetId = item.anchor?.anchor_id;
            if (!targetId) continue;
            const peers = this.anchors.get(targetId) ?? [];
            if (!peers.includes(item.id)) peers.push(item.id);
            this.anchors.set(targetId, peers);
        }
    }

    public updateItems(items: Map<string, ItemData>) {
        this.itemData = items;
        this.structureDirty = true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c791 */
    public markStructureDirty() {
        this.structureDirty = true;
    }

    public setLayout(layout_id: string) {
        this.layout_id = layout_id;
        this.badFontLogged.clear();
        this.publishedSupportedRows.clear();
        this.idSegmentsCache.clear();
        this.structureDirty = true;
    }

    public setDimensions(width: number, height: number) {
        this.width = width;
        this.height = height;
    }

    private idSegments(id: string): number {
        let n = this.idSegmentsCache.get(id);
        if (n === undefined) {
            n = id.split('.').length;
            this.idSegmentsCache.set(id, n);
        }
        return n;
    }

    /** Geometry is integer px on both threads; see docs/EXTRACTED_COMMENTS.md#c792 */
    private static px(n: number): number {
        return Math.trunc(n) || 0;
    }

    public updatePositions(): Map<string, Update[]> {
        if (this.layout_id == null) {
            console.warn("RenderWorker: Renderer has not received a valid layout_id...");
            return new Map<string, Update[]>();
        }
        const updates = new Map<string, Update[]>();
        // see docs/EXTRACTED_COMMENTS.md#c212
        // see docs/EXTRACTED_COMMENTS.md#c791
        if (this.structureDirty) {
            this.rebuildAnchorsFromItems();
            this.evalOrder = this.computeAnchoredEvaluationOrder();
            this.reachability = this.computeAnchorReachability();
            this.buildDedupGroups();
            this.structureDirty = false;
        }
        const result = this.evalOrder;

        // Reset memo for this render tick
        this.layoutRectMemo.clear();
        // see docs/EXTRACTED_COMMENTS.md#c213
        for (const item of this.itemData.values()) {
            if (!item.public_hide_flags) {
                item.public_hide_flags = {...item.hide_flags};
            }
            if (item.hide_flags.render !== false) item.hide_flags.render = false;
            if (item.hide_flags.anchoring !== false) item.hide_flags.anchoring = false;

        }

        this.resolveGeometry(updates, this.layout_id, new Set<string>());

        if (result.ok) {
            for (const anchor of result.order) {
                this.updateHorizontalView(updates, anchor);
            }
        } else {
            console.warn("Anchoring skipped due to invalid anchor graph", result);
        }

        const geomVisited = new Set<string>();
        this.resolveGeometry(updates, this.layout_id, geomVisited);

        this.sweepHorizontalWidths(updates, geomVisited);

        if (result.ok) {
            for (const anchor of result.order) {
                this.updateHorizontalView(updates, anchor);
            }
        }

        this.flowDatasheetRows(updates);

        /* see docs/EXTRACTED_COMMENTS.md#c795 */
        this.clipDatasheetRows(updates);

        this.removeOverlap(updates, this.layout_id, this.layoutRectMemo, this.reachability);
        // see docs/EXTRACTED_COMMENTS.md#c656
        for (const [id, data] of this.itemData.entries()) {
            const desired = data.text ?? "";
            const committed = data.public_text ?? "";
            if (desired === committed) continue;
            this.addUpdate(updates, id, UpdateType.SRC, desired);
            data.public_text = desired;
        }

        this.resolveDedupHiding();
        this.applyChildrenVisibility();

        // see docs/EXTRACTED_COMMENTS.md#c214
        for (const [id, data] of this.itemData.entries()) {
            const currentFlags = data.hide_flags;
            if (!currentFlags) continue;

            const publicFlags = data.public_hide_flags;
            const commitFlags: Partial<typeof currentFlags> = {};

            for (const key of Object.keys(currentFlags) as Array<keyof typeof currentFlags>) {

                if (currentFlags[key] !== publicFlags[key]) {
                    commitFlags[key] = currentFlags[key];
                }
            }

            if (Object.keys(commitFlags).length === 0) continue;
            this.addUpdate(updates, id, UpdateType.HIDE_FLAGS_COMMIT, commitFlags);
            data.public_hide_flags = { ...publicFlags, ...commitFlags};
        }

        return updates;
    }


    /**
     * Overlap, ported from the old DP's DPDataObject.checkOverlapping (webdisplayclient):
     * walking a parent's children from last to first, every still-visible child hides each
     * earlier visible sibling whose box it intersects. A child hidden this way hides nothing
     * itself, so the outcome is a single deterministic pass with no grouping or priority.
     *
     * Deliberate departures from the Java, each covered by RendererOverlap.test.ts:
     * - the whole anchor chain is exempt in both directions (Java compares the chain against
     *   the parent, so only the direct anchor was ever exempt);
     * - no 100-comparison cap per parent;
     * - an `overlay` element is never hidden, but still hides earlier siblings (Java ignored the flag);
     * - the hide is not copied onto descendants: a render-hidden parent already removes its
     *   subtree from the DOM.
     */
    public removeOverlap(
        updates: Map<string, Update[]>,
        id: string,
        memo: Map<string, LayoutRect | null> = this.layoutRectMemo,
        reachability: Map<string, Set<string>> = new Map()
    ) {
        const item = this.itemData.get(id);
        if (!item) return;
        for (const child of item.children) {
            this.removeOverlap(updates, child, memo, reachability);
        }
        // Children of an alternating container never hide each other (Java: isAlternating)
        if (item.type == "DataObjectAlternating") return;
        /* see docs/EXTRACTED_COMMENTS.md#c660 */
        if (item.type == "DataObjectDatasheet") return;

        const children = item.children
            .map((childId) => this.itemData.get(childId))
            .filter((c): c is ItemData => c != null);
        // Elements hidden by data/rule/image/... neither hide nor get hidden (Java: isVisible)
        const shown = (c: ItemData) => !Renderer.isHiddenByNonRender(c) && !c.hide_flags.render;
        const anchoredTogether = (a: string, b: string) =>
            !!(reachability.get(a)?.has(b) || reachability.get(b)?.has(a));

        for (let i = children.length - 1; i >= 0; i--) {
            const child = children[i];
            if (!shown(child)) continue;
            const rect = this.overlapRect(child, memo);
            if (rect.width <= 0 || rect.height <= 0) continue;

            for (let j = i - 1; j >= 0; j--) {
                const sibling = children[j];
                if (!shown(sibling) || sibling.overlay) continue;
                if (anchoredTogether(child.id, sibling.id)) continue;
                if (Renderer.intersects(rect, this.overlapRect(sibling, memo))) {
                    sibling.hide_flags.render = true;
                }
            }
        }
    }

    /** The box overlap compares: the resolved layout rect, else the item's own geometry. */
    private overlapRect(item: ItemData, memo: Map<string, LayoutRect | null>): LayoutRect {
        const layoutRect = this.computeLayoutRect(item.id, memo);
        if (layoutRect) return layoutRect;
        const g = item.geometry;
        return {left: g.x, top: g.y, width: g.width, height: g.height};
    }

    /**
     * Java Rectangle.intersects, verbatim: half-open on both axes, so touching boxes don't
     * overlap; a zero-size box still intersects a box whose span contains its origin.
     */
    private static intersects(a: LayoutRect, b: LayoutRect): boolean {
        return ((b.left <= a.left && b.left + b.width > a.left) || (a.left <= b.left && a.left + a.width > b.left))
            && ((b.top <= a.top && b.top + b.height > a.top) || (a.top <= b.top && a.top + a.height > b.top));
    }

    private spliceExisting(
        updates: Map<string, Update[]>, id: string, type: UpdateType, value: any,
        eq: (existing: any, next: any) => boolean = Object.is
    ): boolean {
        const list = updates.get(id);
        if (!list) return false;
        const idx = list.findIndex(u => u.getUpdateType() === type);
        if (idx === -1) return false;
        if (eq(list[idx].getValue(), value)) return true;
        list.splice(idx, 1);
        if (list.length === 0) updates.delete(id);
        return false;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c655 */
    private publicGeometry(item: ItemData): GeometryCopy {
        if (!item.public_geometry) {
            item.public_geometry = {
                x: item.geometry.x,
                y: item.geometry.y,
                width: item.geometry.width,
                height: item.geometry.height,
            };
        }
        return item.public_geometry;
    }

    public addUpdate(updates: Map<string, Update[]>, id: string, updateType: UpdateType, value: any) {
        const item = this.itemData.get(id);
        if (!item) return;
        switch (updateType) {
            case UpdateType.WIDTH: {
                value = Renderer.px(value);
                const published = this.publicGeometry(item);
                if (published.width === value) return;
                if (this.spliceExisting(updates, id, updateType, value)) return;
                published.width = value;
                break;
            }
            case UpdateType.HEIGHT: {
                value = Renderer.px(value);
                const published = this.publicGeometry(item);
                if (published.height === value) return;
                if (this.spliceExisting(updates, id, updateType, value)) return;
                published.height = value;
                break;
            }
            case UpdateType.X: {
                value = Renderer.px(value);
                const published = this.publicGeometry(item);
                if (published.x === value) return;
                if (this.spliceExisting(updates, id, updateType, value)) return;
                published.x = value;
                break;
            }
            case UpdateType.Y: {
                value = Renderer.px(value);
                const published = this.publicGeometry(item);
                if (published.y === value) return;
                if (this.spliceExisting(updates, id, updateType, value)) return;
                published.y = value;
                break;
            }
            case UpdateType.HIDE_THROUGH_ANCHORING:
                if (item.hide_flags.anchoring === value) return;
                if (this.spliceExisting(updates, id, updateType, value)) return;
                break;
            case UpdateType.HIDE_FLAGS_COMMIT: {
                const flagsEq = (a: any, b: any) => a && b && Object.keys(b).every(k => a[k] === b[k]);
                if (this.spliceExisting(updates, id, updateType, value, flagsEq)) return;
                break;
            }
            case UpdateType.SUPPORTED_ROWS: {
                if (this.publishedSupportedRows.get(id) === value) return;
                if (this.spliceExisting(updates, id, updateType, value)) return;
                this.publishedSupportedRows.set(id, value);
                break;
            }
        }
        const itemUpdates = updates.get(id) || [];
        itemUpdates.push(new Update(id, updateType, value));
        updates.set(id, itemUpdates);
    }

    /**
     * Resolve the HIDE_WHEN_*CHILDREN modes into the renderer-owned `children` hide flag.
     *
     * This used to EMIT a NO_CHILDREN_VISIBLE verdict for the main thread to realize into its
     * own `data` flag via a deferred updateHidden — a four-leg relay (emission → deferred op →
     * geometry re-ingest → commit) where one lost leg latched a hidden parent with a visible
     * child permanently, because every leg's memo then agreed with the stale state (measured on
     * layout 1000, …26.check-in_services_all; again on layout 4, Schiphol SuperFID departures
     * row). Now the outcome is a hide flag this worker owns outright, like render/anchoring/
     * dedup: re-derived from scratch every tick (nothing to latch) and published through the
     * ordinary HIDE_FLAGS_COMMIT diff below, whose delivery self-repairs on the next geometry
     * ingest of the item.
     *
     * Depth-descending order makes the flag itself carry the cascade: a CHILDREN-mode child has
     * its own `children` flag finalized before any ancestor counts it, so "all my children are
     * hidden because THEIR children are hidden" needs no side table.
     *
     * Mode composition (the v-if ORs all published flags):
     *  - HIDE_WHEN_NO_CHILDREN:              children = childrenCond
     *  - HIDE_WHEN_NO_DATA_OR_NO_CHILDREN:   children = childrenCond   (main stages `data` for
     *                                        the no-data half; the v-if's OR composes them)
     *  - HIDE_WHEN_NO_DATA_AND_NO_CHILDREN:  children = childrenCond && no_data — neither half
     *                                        may hide alone, so main never stages `data` for
     *                                        this mode and ships the no-data verdict as the
     *                                        `no_data` INPUT on the geometry snapshot instead.
     */
    private applyChildrenVisibility() {
        const candidates: ItemData[] = [];
        for (const item of this.itemData.values()) {
            const autoHide = item.auto_hide;
            if (
                autoHide !== "HIDE_WHEN_NO_CHILDREN" &&
                autoHide !== "HIDE_WHEN_NO_DATA_AND_NO_CHILDREN" &&
                autoHide !== "HIDE_WHEN_NO_DATA_OR_NO_CHILDREN"
            ) {
                continue;
            }
            candidates.push(item);
        }
        candidates.sort((a, b) => this.idSegments(b.id) - this.idSegments(a.id));

        for (const item of candidates) {
            const autoHide = item.auto_hide;

            // An unresolved container must not be children-hidden — same guard getNewState
            // applied when this decision lived on the main thread (see resolveContainerTemplate).
            let childrenCond: boolean;
            if (item.template_resolved === false) {
                childrenCond = false;
            } else {
                let visibleCount = 0;
                for (const childId of item.children) {
                    const child = this.itemData.get(childId);
                    // A child absent from our mirror does not count as visible.
                    if (!child) continue;
                    const hf = child.hide_flags;
                    if (hf.data || hf.rule || hf.image
                        || hf.anchoring || hf.render || hf.dedup || hf.playlist || hf.timer || hf.children) continue;
                    visibleCount++;
                }
                childrenCond = visibleCount === 0;
            }

            item.hide_flags.children = autoHide === "HIDE_WHEN_NO_DATA_AND_NO_CHILDREN"
                ? childrenCond && item.no_data === true
                : childrenCond;
        }
    }


    /* see docs/EXTRACTED_COMMENTS.md#c589 */
    private resolveGeometry(
        updates: Map<string, Update[]>,
        id: string,
        visited: Set<string>
    ) {
        if (visited.has(id)) return;
        visited.add(id);
        const item = this.itemData.get(id);
        if (!item) return;

        this.sizeDownward(updates, item);
        for (const childID of item.children) {
            const child = this.itemData.get(childID);
            if (!child) continue;
            if (item.type !== "DataObjectDatasheet") {
                if (child.hide_flags.data) continue;
                if (child.hide_flags.rule) continue;
                if (child.hide_flags.children) continue;
            }
            this.resolveGeometry(updates, childID, visited);
        }
        this.sizeUpward(updates, item);

        const parentId = getParent(item.id);
        if (parentId != null) {
            const parent = this.itemData.get(parentId);
            if (parent?.view_type === "TEXT_VIEW_EXTEND_HORIZONTAL") {
                this.updateWidth(updates, item);
            }
        }
        if (item.view_type === "TEXT_VIEW_EXTEND_HORIZONTAL" && item.children.length === 0) {
            this.updateWidth(updates, item);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c684 */
    private flowDatasheetRows(updates: Map<string, Update[]>) {
        for (const [, item] of this.itemData) {
            if (item.type !== "DataObjectDatasheet") continue;
            if (!item.variable_rows || item.children.length === 0) continue;
            /* see docs/EXTRACTED_COMMENTS.md#c692 */
            const firstRow = this.itemData.get(item.children[0]);
            if (!firstRow) continue;
            let cursor = firstRow.geometry.original.y;
            for (const childID of item.children) {
                const child = this.itemData.get(childID);
                if (!child) continue;
                const y = Renderer.px(cursor);
                if (child.geometry.y !== y) {
                    this.addUpdate(updates, childID, UpdateType.Y, y);
                    child.geometry.y = y;
                }
                /* see docs/EXTRACTED_COMMENTS.md#c685 */
                cursor += child.geometry.height + (child.geometry.borderY || 0);
            }
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c590 */
    private clipDatasheetRows(updates: Map<string, Update[]>) {
        for (const [, item] of this.itemData) {
            if (item.type !== "DataObjectDatasheet" || item.children.length === 0) continue;
            const flowed = !!item.variable_rows;
            const parentHeight = item.geometry.height;
            if (parentHeight <= 0) continue;
            /* see docs/EXTRACTED_COMMENTS.md#c694 */
            const mayHide = flowed || !RenderTraversalUtils.hasPendingDescendant(item.id);

            let supported = 0;
            let lastFitBottom = 0;
            let lastFitHeight = 0;
            for (const childID of item.children) {
                const child = this.itemData.get(childID);
                if (!child) continue;
                const childTop = child.geometry.y;
                const childHeight = child.geometry.height;
                const childBottom = childTop + childHeight;
                /* see docs/EXTRACTED_COMMENTS.md#c687 */
                if (flowed) {
                    if ((childBottom) > parentHeight) child.hide_flags.render = true;
                    /* see docs/EXTRACTED_COMMENTS.md#c748 */
                    if (childHeight > 0 && childBottom <= parentHeight) {
                        supported++;
                        lastFitBottom = childBottom;
                        lastFitHeight = childHeight;
                    }
                } else if (childBottom > parentHeight) {
                    if (mayHide) child.hide_flags.render = true;
                } else {
                    supported++;
                }
            }
            /* see docs/EXTRACTED_COMMENTS.md#c748 */
            if (flowed && lastFitHeight > 0) {
                supported += Math.floor((parentHeight - lastFitBottom) / lastFitHeight);
            }
            /* see docs/EXTRACTED_COMMENTS.md#c662 */
            this.addUpdate(updates, item.id, UpdateType.SUPPORTED_ROWS, supported);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c591 */
    private buildDedupGroups(): void {
        this.dedupGroups = [];
        const groupMap = new Map<string, string[]>();

        for (const [id, item] of this.itemData) {
            if (!item.omit_duplicate) continue;

            let datasheetId: string | null = null;
            // Terminate on any falsy parent, not just null: this walk must not depend on
            // getParent's no-parent sentinel. The ancestor chain is only as complete as
            // itemData, so a row whose datasheet parent has not been ingested yet walks all
            // the way to the root — and a root that reports itself as its own parent spins
            // this loop forever, wedging the whole worker thread mid-flush.
            for (let currentId = id; ;) {
                const parentId = getParent(currentId);
                if (!parentId) break;
                const parent = this.itemData.get(parentId);
                if (parent && parent.type === "DataObjectDatasheet") {
                    datasheetId = parentId;
                    break;
                }
                currentId = parentId;
            }
            if (!datasheetId) continue;

            const datasheetPrefix = datasheetId + ".";
            if (!id.startsWith(datasheetPrefix)) continue;
            const suffix = id.slice(datasheetPrefix.length);
            const firstDot = suffix.indexOf(".");
            if (firstDot === -1) continue;
            const templateSuffix = suffix.slice(firstDot + 1);

            const key = datasheetId + ":" + templateSuffix;
            if (!groupMap.has(key)) groupMap.set(key, []);
            groupMap.get(key)!.push(id);
        }

        for (const [key, members] of groupMap) {
            const datasheetId = key.slice(0, key.indexOf(':'));
            members.sort((a, b) => {
                const ar = parseInt(a.slice(datasheetId.length + 1).split('.')[0], 10);
                const br = parseInt(b.slice(datasheetId.length + 1).split('.')[0], 10);
                return ar - br;
            });
            this.dedupGroups.push({ key, members });
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c592 */
    private resolveDedupHiding(): void {
        for (const item of this.itemData.values()) {
            if (item.hide_flags.dedup !== false) item.hide_flags.dedup = false;
        }
        for (const group of this.dedupGroups) {
            let firstSrc: string | null = null;

            for (const memberId of group.members) {
                const item = this.itemData.get(memberId);
                if (!item) continue;

                // see docs/EXTRACTED_COMMENTS.md#c593
                let nextDedup = false;

                const hiddenByOther =
                    item.hide_flags.data || item.hide_flags.rule ||
                    item.hide_flags.image || item.hide_flags.alternating ||
                    item.hide_flags.render || item.hide_flags.anchoring ||
                    item.hide_flags.playlist || item.hide_flags.countdown ||
                    item.hide_flags.timer || item.hide_flags.children;

                if (hiddenByOther) {
                    // Skip from dedup chain without disturbing firstSrc.
                } else if (!item.public_text || item.public_text === "") {
                    // see docs/EXTRACTED_COMMENTS.md#c594
                } else if (firstSrc === null) {
                    firstSrc = item.public_text;
                } else if (item.public_text == firstSrc) {
                    nextDedup = true;
                } else {
                    firstSrc = item.public_text;
                }
                item.hide_flags.dedup = nextDedup;
            }
        }
    }


    private static parseAutoSize(mode: string): { width: "parent" | "child" | "none"; height: "parent" | "child" | "none" } {
        if (!mode || mode === "AUTOSIZE_NONE") return { width: "none", height: "none" };
        return {
            width: mode.includes("PARENT_WIDTH") ? "parent" : mode.includes("CHILD_WIDTH") ? "child" : "none",
            height: mode.includes("PARENT_HEIGHT") ? "parent" : mode.includes("CHILD_HEIGHT") ? "child" : "none",
        };
    }

    private static isHiddenByNonRender(item?: ItemData | null): boolean {
        if (!item) return true;
        return (item.hide_flags.data || item.hide_flags.rule || item.hide_flags.alternating || item.hide_flags.image || item.hide_flags.playlist || item.hide_flags.countdown || item.hide_flags.timer || item.hide_flags.children);
    }

    private resolveAxis(updates: Map<string, Update[]>, item: ItemData, axis: "width" | "height", source: "parent" | "child") {
        const type = axis === "width" ? UpdateType.WIDTH : UpdateType.HEIGHT;
        const value = this.getSize(item, axis, source);

        if (value == null) return;

        const px = Renderer.px(value);
        this.addUpdate(updates, item.id, type, px);
        if (axis === "width") item.geometry.width = px;
        else item.geometry.height = px;
    }

    private sizeUpward(updates: Map<string, Update[]>, item: ItemData) {
        const { width, height } = Renderer.parseAutoSize(item.auto_size);
        if (width === "child") this.resolveAxis(updates, item, "width", "child");
        if (height === "child") this.resolveAxis(updates, item, "height", "child");
    }

    private sizeDownward(updates: Map<string, Update[]>, item: ItemData) {
        if (!item) return;
        const { width, height } = Renderer.parseAutoSize(item.auto_size);

        if (width === "parent") this.resolveAxis(updates, item, "width", "parent");
        if (height === "parent") this.resolveAxis(updates, item, "height", "parent");
    }

    private getSize(item: ItemData, axis: "width" | "height", source: "parent" | "child"): number | null {
        const parent = getParent(item.id);
        const parentElement = parent != null ? this.itemData.get(parent) : null;

        if (axis === "width" && source === "parent") {
            if (item.children.length == 0 && item.view_type?.includes("HORIZONTAL")) {
                return item.geometry.width;
            }
        }

        if (source === "child") {
            // see docs/EXTRACTED_COMMENTS.md#c596
            /* see docs/EXTRACTED_COMMENTS.md#c747 */
            const children = RenderTraversalUtils.sizingChildren(item, this.itemData);
            if (axis === "width") {
                const width = item.geometry.original.width;
                if (children.length === 0) return width;
                const {maxWidth, maxXOffset} = RenderTraversalUtils.maxChildWidthWithOffset(children);
                return maxWidth + maxXOffset + width;
            }
            const height = item.geometry.original.height;
            if (children.length === 0) return height;
            const {maxHeight, maxYOffset} = RenderTraversalUtils.maxChildHeightWithOffset(children);
            return maxHeight + maxYOffset + height;
        }

        if (axis === "width") {
            let parentWidth = 0;
            if (parent == null) {
                parentWidth = this.width;
            } else if (parentElement != null) {
                parentWidth = parentElement.geometry.width;
            }
            return parentWidth - item.geometry.x + item.geometry.original.width - item.geometry.borderX;
        }

        let parentHeight = 0;
        if (parent == null) {
            parentHeight = this.height;
        } else if (parentElement != null) {
            parentHeight = parentElement.geometry.height;
        }
        return parentHeight - item.geometry.y + item.geometry.original.height - item.geometry.borderY;
    }

    private computeLayoutRect(
        id: string,
        memo: Map<string, LayoutRect | null>,
        visiting: Set<string> = new Set<string>()
    ): LayoutRect | null {
        if (memo.has(id)) {
            return memo.get(id)!;
        }
        if (visiting.has(id)) {
            memo.set(id, null);
            return null;
        }
        visiting.add(id);

        const item = this.itemData.get(id);
        if (!item) {
            memo.set(id, null);
            visiting.delete(id);
            return null;
        }

        // `alternating` is deliberately absent: an alternating-hidden child is only waiting for its
        // turn (and still on screen while it fades out). Dropping its rect collapsed every anchor
        // chain inside it onto its origin, so a CHILD-sized ancestor shrank and the subtree faded
        // back in at the wrong size. updateHorizontalView still hides items anchored directly to it.
        const hiddenByNonRender =
            item.hide_flags.data ||
            item.hide_flags.rule ||
            item.hide_flags.image ||
            item.hide_flags.playlist ||
            item.hide_flags.countdown ||
            item.hide_flags.children;
        if (hiddenByNonRender) {
            memo.set(id, null);
            visiting.delete(id);
            return null;
        }

        const width = item.geometry.width;
        const height = item.geometry.height;
        const parentId = getParent(item.id);

        if (item.geometry.position === "absolute" || parentId == null) {
            const rect: LayoutRect = {
                left: item.geometry.x,
                top: item.geometry.y,
                width,
                height,
            };
            memo.set(id, rect);
            visiting.delete(id);
            return rect;
        }

        const parentRect = this.computeLayoutRect(parentId, memo, visiting);
        if (!parentRect) {
            memo.set(id, null);
            visiting.delete(id);
            return null;
        }

        const rect: LayoutRect = {
            left: parentRect.left + item.geometry.x,
            top: parentRect.top + item.geometry.y,
            width,
            height,
        };
        memo.set(id, rect);
        visiting.delete(id);
        return rect;
    }

    private computeAnchorReachability(): Map<string, Set<string>> {
        const adjacency: Map<string, string[]> = new Map();
        for (const [src, targets] of this.anchors) {
            adjacency.set(src, [...(targets ?? [])]);
            for (const child of targets ?? []) {
                if (!adjacency.has(child)) {
                    adjacency.set(child, []);
                }
            }
        }

        const memo: Map<string, Set<string>> = new Map();
        const visit = (id: string): Set<string> => {
            if (memo.has(id)) return memo.get(id)!;
            const seen = new Set<string>();
            const stack = [...(adjacency.get(id) ?? [])];
            while (stack.length) {
                const next = stack.pop()!;
                if (seen.has(next)) continue;
                seen.add(next);
                for (const neighbor of adjacency.get(next) ?? []) {
                    if (!seen.has(neighbor)) {
                        stack.push(neighbor);
                    }
                }
            }
            memo.set(id, seen);
            return seen;
        };

        for (const id of adjacency.keys()) {
            visit(id);
        }
        return memo;
    }

    private fallbackRect(anchorParentId: string | null, memo: Map<string, LayoutRect | null>): LayoutRect {
        if (anchorParentId == null) {
            return {left: 0, top: 0, width: 0, height: 0};
        }
        const parentRect = this.computeLayoutRect(anchorParentId, memo);
        if (parentRect) {
            return parentRect;
        }
        return {left: 0, top: 0, width: 0, height: 0};
    }


    private updateHorizontalView(
        updates: Map<string, Update[]>,
        parentId: string
    ) {
        const parent = this.itemData.get(parentId);
        if (!parent?.anchor) return;
        parent.hide_flags.anchoring = false;
        const memo = new Map<string, LayoutRect | null>();
        const {anchor_id, anchor_location} = parent.anchor;
        const anchorRect =
            this.computeLayoutRect(anchor_id, memo) ??
            this.fallbackRect(getParent(anchor_id), memo);

        if (!anchorRect) return;

        const anchor = this.itemData.get(anchor_id);
        // see docs/EXTRACTED_COMMENTS.md#c597
        if (anchor && (anchor.hide_flags.rule || anchor.hide_flags.data || anchor.hide_flags.anchoring || anchor.hide_flags.image || anchor.hide_flags.alternating || anchor.hide_flags.playlist || anchor.hide_flags.countdown || anchor.hide_flags.children)) {
            parent.hide_flags.anchoring = true;
        }

        const offsetX = parent.geometry.original.x;
        const offsetY = parent.geometry.original.y;
        const loc = (anchor_location || "").toUpperCase();
        const x = Renderer.px(anchorRect.left + (loc.includes("LEFT") ? 0 : anchorRect.width) + offsetX);
        const y = Renderer.px(anchorRect.top + (loc.includes("BOTTOM") ? anchorRect.height : 0) + offsetY);

        this.addUpdate(updates, parentId, UpdateType.X, x);
        parent.geometry.x = x;
        this.addUpdate(updates, parentId, UpdateType.Y, y);
        parent.geometry.y = y;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c599 */
    private computeAnchoredEvaluationOrder(): EvalOrderResult {
        // see docs/EXTRACTED_COMMENTS.md#c600
        const S = new Set<string>();
        for (const [p, list] of this.anchors) {
            if (!this.itemData.has(p)) continue;
            S.add(p);
            for (const c of list ?? []) S.add(c);
        }


        // 3) Build graph restricted to S
        const children: Map<string, string[]> = new Map();
        const indeg: Map<string, number> = new Map();
        const parentCount: Map<string, number> = new Map();

        for (const id of S) {
            if (!this.itemData.has(id)) continue;
            children.set(id, []);
            indeg.set(id, 0);
            parentCount.set(id, 0);
        }

        for (const [p, list] of this.anchors) {
            if (!S.has(p)) continue; // not in induced set (shouldn’t happen, but safe)
            if (!children.has(p)) {
                return {ok: false, reason: "unknown_id", details: [p]};
            }
            const arr = children.get(p)!;
            for (const c of list ?? []) {
                if (!S.has(c)) continue; // ignore children outside S (shouldn’t happen)
                arr.push(c);
                indeg.set(c, 1); // each child has exactly one parent in this model
                parentCount.set(c, (parentCount.get(c)! + 1));
                if (parentCount.get(c)! > 1) {
                    return {ok: false, reason: "multiple_parents", details: [c]};
                }
            }
        }

        // 4) Kahn’s algorithm on the induced subgraph
        const q: string[] = [];
        for (const [id, k] of indeg) if (k === 0) q.push(id);

        const order: string[] = [];
        while (q.length) {
            const p = q.pop()!;
            if (!children.has(p)) continue;
            order.push(p);
            for (const ch of children.get(p)!) {
                if (indeg.get(ch)! > 0) {
                    indeg.set(ch, 0);
                    q.push(ch);
                }
            }
        }

        // 5) Cycle check
        if (order.length !== S.size) {
            const leftovers: string[] = [];
            for (const [id, k] of indeg) if (k !== 0) leftovers.push(id);
            if (leftovers.length > 0)
                return {ok: false, reason: "cycle_detected", details: leftovers};
        }

        return {ok: true, order};
    }

    public deleteItems(ids: string[]) {
        for (const id of ids) {
            this.itemData.delete(id);
            this.layoutRectMemo.delete(id);
            this.badFontLogged.delete(id);
            this.idSegmentsCache.delete(id);
        }
        this.structureDirty = true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c601 */
    private isHorizontalWidthOwner(item: ItemData): boolean {
        if (item.view_type === "TEXT_VIEW_EXTEND_HORIZONTAL" && item.children.length === 0) return true;
        const parentId = getParent(item.id);
        const parent = parentId != null ? this.itemData.get(parentId) : null;
        return parent?.view_type === "TEXT_VIEW_EXTEND_HORIZONTAL";
    }

    /* see docs/EXTRACTED_COMMENTS.md#c602 */
    private sweepHorizontalWidths(updates: Map<string, Update[]>, visited: Set<string>) {
        for (const item of this.itemData.values()) {
            if (visited.has(item.id)) continue;
            if (!this.isHorizontalWidthOwner(item)) continue;
            this.updateWidth(updates, item);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c657 */
    private applyMeasureFont(font: string, id: string): boolean {
        if (!this.measureCtx) return false;

        // Per spec an unparseable assignment to ctx.font is IGNORED, silently leaving whatever
        // the PREVIOUS item set. The sentinel makes that detectable: if the real assignment did
        // not take, the readback is still the sentinel.
        const SENTINEL = "10px sans-serif";
        if (font !== SENTINEL) {
            this.measureCtx.font = SENTINEL;
        }
        this.measureCtx.font = font;
        const applied = this.measureCtx.font;

        if (font !== SENTINEL && applied === SENTINEL) {
            if (!this.badFontLogged.has(id)) {
                this.badFontLogged.add(id);
                console.warn(`[Renderer] unparseable font for ${id} (${font}) — width not published`);
            }
            return false;
        }

        // An upstream style with no font-size becomes "0pt" (cssFontSize), which parses cleanly
        // and measures every string as ~0 wide — the element collapses to its base width and both
        // threads then agree on that wrong number, so nothing re-emits it.
        const size = parseFloat(/(\d*\.?\d+)\s*(px|pt|em|rem|%)/.exec(applied)?.[1] ?? "0");
        if (!(size > 0)) {
            if (!this.badFontLogged.has(id)) {
                this.badFontLogged.add(id);
                console.warn(`[Renderer] zero font-size for ${id} (${applied}) — width not published`);
            }
            return false;
        }

        this.badFontLogged.delete(id);
        return true;
    }

    private updateWidth(updates: Map<string, Update[]>, item: ItemData) {
        if (!item.font) return;
        const text = item.text;

        /* see docs/EXTRACTED_COMMENTS.md#c700 */
        /* see docs/EXTRACTED_COMMENTS.md#c794 */
        const mainMeasured = item.textAdvanceFor === text ? item.textAdvance : undefined;
        let usedWidth: number;
        if (mainMeasured != null && mainMeasured > 0) {
            usedWidth = mainMeasured;
        } else {
            if (!this.measureCtx) return;
            if (!this.applyMeasureFont(item.font, item.id)) return;
            const metrics = this.measureCtx.measureText(text || " ");
            const inkWidth = (metrics.actualBoundingBoxLeft ?? 0) + (metrics.actualBoundingBoxRight ?? 0);
            usedWidth = Math.max(metrics.width, inkWidth);
        }

        const newWidth = Math.ceil(usedWidth) + item.geometry.original.width;
        if (newWidth <= 0) return;
        this.addUpdate(updates, item.id, UpdateType.WIDTH, newWidth);
        item.geometry.width = newWidth;
    }

}

export class Update {
    private readonly itemID: string;
    private readonly updateType: UpdateType;
    private readonly value: any;

    constructor(itemID: string, updateType: UpdateType, value: any) {
        this.itemID = itemID;
        this.updateType = updateType;
        this.value = value;
    }

    public getItemID() {
        return this.itemID;
    }

    public getUpdateType() {
        return this.updateType;
    }

    public getValue() {
        return this.value;
    }
}

/* see docs/EXTRACTED_COMMENTS.md#c790 */
/* see docs/EXTRACTED_COMMENTS.md#c796 */
export enum UpdateType {
    X = "x", Y = "y", HEIGHT = "height", WIDTH = "width",
    HIDE_THROUGH_ANCHORING = "hide_through_anchoring",
    HIDE_FLAGS_COMMIT = "hide_flags_commit",
    SRC = "src",
    SUPPORTED_ROWS = "supported_rows",
}
